import React, { useState } from 'react';
import { Grid, Alert, Stepper, Step, StepLabel } from '@mui/material';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import GridContainer from 'components/GridContainer';
import Spacing from 'components/Spacing';
import FormWithStateContextProvider from 'contexts/FormWithStateContext';
import { useParams } from 'react-router-dom';
import usePartnerTransfersForm from './hooks/usePartnerTransfersForm';
import AddTransfer from './AddTransfer';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { useLayoutContext } from 'contexts/LayoutContext';
import TransferActions from './Editor/TransferActions';
import Review from './Review';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import TransferLinkedCapitalActions from './Editor/TransferLinkedCapitalActions';
import api from 'lib/trpcClient';
import PageLoader from 'components/PageLoader';
import TransferGeneralCapitalActions from './Editor/TransferGeneralCapitalActions';
import TransferAdvanceSubscriptions from './Editor/TransferAdvanceSubscriptions';
import TransferFees from './Editor/TransferFees';
import SuccessPage from './SuccessPage';

enum Steps {
  TransferDetails = 0,
  Investments = 1,
  Income = 2,
  Expenses = 3,
  Fees = 4,
  LinkedCapitalActions = 5,
  GeneralCapitalActions = 6,
  AdvanceSubscriptions = 7,
  Review = 8,
  Success = 9,
}

const stepLabels: Record<Steps, string> = {
  [Steps.TransferDetails]: 'Add partner transfer',
  [Steps.Investments]: 'Investments',
  [Steps.Income]: 'Income',
  [Steps.Expenses]: 'Expenses',
  [Steps.Fees]: 'Fees',
  [Steps.LinkedCapitalActions]: 'Linked capital actions',
  [Steps.GeneralCapitalActions]: 'General capital actions',
  [Steps.AdvanceSubscriptions]: 'Advance subscriptions',
  [Steps.Review]: 'Review',
  [Steps.Success]: 'Done',
};

const AddPartnerTransfer: React.FC = () => {
  const [activeStep, setActiveStep] = useState<Steps>(Steps.TransferDetails);
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle, isLoading: vehicleLoading } = useVehicle(selectedVehicleId);

  // Load existing partner transfer data if editing
  const { data: existingPartnerTransfer, isLoading: isLoadingPartnerTransfer } =
    api.partnerTransfers.getById.useQuery(
      {
        partnerTransferId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
      },
      {
        enabled: !!id && !!selectedVehicleId,
        staleTime: 0,
        gcTime: 0,
      },
    );

  // Prepare default values from loaded data
  const formDefaultValues =
    !existingPartnerTransfer || !id
      ? undefined
      : {
          partnerTransferId: id,
          trasferrorCommitmentId:
            existingPartnerTransfer.transferorCommitmentId,
          transferDate: existingPartnerTransfer.transferDate,
          transferees: existingPartnerTransfer.newCommitments.transferees.map(
            (transferee) => ({
              investorLabel: transferee.investorName,
              investorId: transferee.investorId,
              amountLcy: transferee.amountLcy,
              percentage: transferee.percentage,
            }),
          ),
        };

  const { formMethods } = usePartnerTransfersForm({
    defaultValues: formDefaultValues,
  });

  const currentPartnerTransferId =
    id || formMethods.getValues('partnerTransferId') || '';

  const pageTitle = currentPartnerTransferId
    ? 'Partner transfers: Edit partner transfer'
    : 'Partner transfers: Create partner transfer';

  const handleNext = (newPartnerTransferId?: string) => {
    if (newPartnerTransferId) {
      const newUrl = `/partner-transfers/${newPartnerTransferId}`;
      window.history.replaceState(null, '', newUrl);
    }
    setActiveStep((prev) => prev + 1);
  };

  if (activeStep === Steps.Success) {
    return (
      <PageContainer heading={pageTitle}>
        <SuccessPage />
      </PageContainer>
    );
  }

  const initialState = {
    formMethods,
  };

  // Show loader while fetching data in edit mode
  if (id && isLoadingPartnerTransfer) {
    return <PageLoader />;
  }

  if (!vehicle) {
    return (
      <PageContainer loading={vehicleLoading} heading={pageTitle}>
        <PageContent>
          <Spacing size="lg">
            <Alert severity="error">Vehicle not found.</Alert>
          </Spacing>
        </PageContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer loading={vehicleLoading} heading={pageTitle}>
      <PageContent>
        <Spacing size="lg">
          <GridContainer>
            <Grid size={{ sm: 12, md: 12, lg: 8 }}>
              <Stepper activeStep={activeStep}>
                {Object.values(stepLabels).map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Grid>
          </GridContainer>

          <FormWithStateContextProvider
            formMethods={formMethods}
            initialState={initialState}
          >
            {activeStep === Steps.TransferDetails && (
              <>
                <AddTransfer
                  onNext={handleNext}
                  id={id}
                  isSubmitting={formMethods.formState.isSubmitting}
                  baseCurrency={vehicle.baseCurrency}
                  vehicleId={vehicle._id}
                />
              </>
            )}
            {activeStep === Steps.Investments && (
              <TransferActions
                partnerTransferSection={PartnerTransferSection.Investments}
                partnerTransferId={currentPartnerTransferId}
                vehicleId={vehicle._id}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={() => setActiveStep(Steps.TransferDetails)}
                onNext={() => setActiveStep(Steps.Income)}
              />
            )}
            {activeStep === Steps.Income && (
              <TransferActions
                partnerTransferSection={PartnerTransferSection.Income}
                partnerTransferId={currentPartnerTransferId}
                vehicleId={vehicle._id}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={() => setActiveStep(Steps.Investments)}
                onNext={() => setActiveStep(Steps.Expenses)}
              />
            )}
            {activeStep === Steps.Expenses && (
              <TransferActions
                partnerTransferSection={PartnerTransferSection.Expenses}
                partnerTransferId={currentPartnerTransferId}
                vehicleId={vehicle._id}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={() => setActiveStep(Steps.Income)}
                onNext={() => setActiveStep(Steps.Fees)}
              />
            )}
            {activeStep === Steps.Fees && (
              <TransferFees
                partnerTransferId={currentPartnerTransferId}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={() => setActiveStep(Steps.Expenses)}
                onNext={() => setActiveStep(Steps.LinkedCapitalActions)}
              />
            )}
            {activeStep === Steps.LinkedCapitalActions && (
              <TransferLinkedCapitalActions
                partnerTransferId={currentPartnerTransferId}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={() => setActiveStep(Steps.Fees)}
                onNext={() => setActiveStep(Steps.GeneralCapitalActions)}
              />
            )}
            {activeStep === Steps.GeneralCapitalActions && (
              <TransferGeneralCapitalActions
                partnerTransferId={currentPartnerTransferId}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={() => setActiveStep(Steps.LinkedCapitalActions)}
                onNext={() => setActiveStep(Steps.AdvanceSubscriptions)}
              />
            )}
            {activeStep === Steps.AdvanceSubscriptions && (
              <TransferAdvanceSubscriptions
                partnerTransferId={currentPartnerTransferId}
                baseCurrency={vehicle.baseCurrency}
                onPrevious={() => setActiveStep(Steps.GeneralCapitalActions)}
                onNext={() => setActiveStep(Steps.Review)}
              />
            )}

            {activeStep === Steps.Review && (
              <Review
                onPrevious={() => setActiveStep(Steps.AdvanceSubscriptions)}
                onApprove={() => setActiveStep(Steps.Success)}
              />
            )}
          </FormWithStateContextProvider>
        </Spacing>
      </PageContent>
    </PageContainer>
  );
};

export default AddPartnerTransfer;
